import type {
  ActionsReportData,
  AiReportData,
  AiReportType,
  AllAiReports,
  AnswersReportData,
  AssignmentIntegrityReport,
  ReportData,
  TranscribeReportData,
} from "@/types/endpoints/reports/GetAiReportById";
import type {
  PostAiReportFeedbackRequest,
  PostAiReportFeedbackResponse,
} from "@/types/endpoints/reports/PostAiReportFeedback";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { ACCESS_TOKEN_KEY } from "../base/private/axiosPrivateClient";

const ratingBaseQuery = fetchBaseQuery({
  baseUrl: `${process.env.NEXT_PUBLIC_API_RATING_URL}reports/ai/`,
  prepareHeaders: (headers) => {
    const token =
      typeof window !== "undefined"
        ? localStorage.getItem(ACCESS_TOKEN_KEY)
        : null;

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    return headers;
  },
});

/** Параметры для запроса AI отчёта */
export interface GetAiReportParams {
  attemptId: number;
  reportType: AiReportType;
  forceRegenerate?: boolean;
  baseReportTypes?: string[];
}

/** Trust Score по попытке. trust_score: 0..100 или null, если evaluation-отчёт ещё не готов */
export interface TrustScoreEntity {
  attempt_id: number;
  assignment_id: number;
  student_id: number;
  trust_score: number | null;
}

/** Параметры для запроса всех AI отчётов */
export interface GetAllAiReportsParams {
  attemptId: number;
  forceRegenerate?: boolean;
}

/** Список всех типов отчётов */
const AI_REPORT_TYPES: AiReportType[] = [
  'actions',
  'answers',
  'transcribe',
  'q_density',
  'evaluation',
];

/** Report types fetched from the rating API (excludes transcribe which uses trust API) */
const RATING_REPORT_TYPES: AiReportType[] = [
  'actions',
  'answers',
  'q_density',
  'evaluation',
];

export const reportApi = createApi({
  reducerPath: "reportApi",
  baseQuery: ratingBaseQuery,
  tagTypes: ["reports"],
  keepUnusedDataFor: 300,
  endpoints: (builder) => ({
    /**
     * Получить AI отчёт по ID попытки (legacy endpoint)
     * @deprecated Используйте getAiReport с указанием типа
     */
    getAiReportById: builder.query<ReportData, { attemptId: number }>({
      query: ({ attemptId }) => ({
        url: `${attemptId}`,
        method: "GET",
      }),
      transformResponse: (response: ReportData[] | ReportData): ReportData => {
        if (Array.isArray(response)) return response[0];
        return response;
      },
      providesTags: (result, error, arg) =>
        result ? [{ type: "reports", id: arg.attemptId }] : ["reports"],
    }),

    /**
     * Получить AI отчёт определённого типа
     */
    getAiReport: builder.query<AiReportData, GetAiReportParams>({
      query: ({ attemptId, reportType, forceRegenerate = false, baseReportTypes }) => {
        const params = new URLSearchParams();
        params.append("force_regenerate", String(forceRegenerate));
        if (baseReportTypes?.length) {
          baseReportTypes.forEach(type => params.append("base_report_types", type));
        }
        return {
          url: `/${reportType}/${attemptId}?${params.toString()}`,
          method: "GET",
        };
      },
      onQueryStarted(arg, { queryFulfilled }) {
        queryFulfilled
          .catch((error: any) => {
            const status = error?.error?.status ?? error?.status;
            if (status === 404) {
              console.warn(`Report endpoint not found: ${arg.reportType}/${arg.attemptId}`);
            }
          });
      },
      transformResponse: (response: AiReportData[] | AiReportData): AiReportData => {
        if (Array.isArray(response)) return response[0];
        return response;
      },
      providesTags: (result, error, { attemptId, reportType }) =>
        result
          ? [{ type: "reports", id: `${reportType}-${attemptId}` }]
          : ["reports"],
    }),

    /**
     * Получить все AI отчёты для попытки
     * Запрашивает все типы отчётов параллельно
     */
    getAllAiReports: builder.query<AllAiReports, GetAllAiReportsParams>({
      async queryFn({ attemptId, forceRegenerate = false }, _api, _extra, baseQuery) {
        const results: AllAiReports = {
          actions: null,
          answers: null,
          transcribe: null,
          q_density: null,
          evaluation: null,
        };

        const fetchPromises = RATING_REPORT_TYPES.map(async (reportType) => {
          try {
            const params = new URLSearchParams();
            params.append('force_regenerate', String(forceRegenerate));

            const response = await baseQuery({
              url: `reports/ai/${reportType}/${attemptId}?${params.toString()}`,
              method: "GET",
            });

            if (response.data) {
              const data = response.data as AiReportData[] | AiReportData;
              const reportData = Array.isArray(data) ? data[0] : data;

              // Присваиваем с правильным типом
              switch (reportType) {
                case 'actions':
                  results.actions = reportData as ActionsReportData;
                  break;
                case 'answers':
                  results.answers = reportData as AnswersReportData;
                  break;
                case 'transcribe':
                  results.transcribe = reportData as TranscribeReportData;
                  break;
                case 'q_density':
                  results.q_density = reportData;
                  break;
                case 'evaluation':
                  results.evaluation = reportData;
                  break;
              }
            }
          } catch (error) {
            console.warn(`Failed to fetch ${reportType} report for attempt ${attemptId}:`, error);
          }
        });

        await Promise.allSettled(fetchPromises);

        return { data: results };
      },
      providesTags: (result, error, { attemptId }) =>
        result
          ? [
            { type: "reports", id: `all-${attemptId}` },
            ...AI_REPORT_TYPES.map(type => ({ type: "reports" as const, id: `${type}-${attemptId}` })),
          ]
          : ["reports"],
    }),

    /**
     * Trust Score по одной попытке.
     * Возвращает объект с trust_score (0..100) или null если evaluation-отчёт не готов.
     */
    getTrustScoreByAttempt: builder.query<TrustScoreEntity | null, { attemptId: number }>({
      query: ({ attemptId }) => ({
        url: `trust-scores/attempt/${attemptId}`,
        method: "GET",
      }),
      providesTags: (result, error, { attemptId }) => [
        { type: "reports", id: `trust-score-attempt-${attemptId}` },
      ],
    }),

    /**
     * Trust Scores по всем попыткам задания.
     * Возвращает массив (пустой если ни одного evaluation-отчёта).
     */
    getTrustScoresByAssignment: builder.query<TrustScoreEntity[], { assignmentId: number }>({
      query: ({ assignmentId }) => ({
        url: `trust-scores/assignment/${assignmentId}`,
        method: "GET",
      }),
      providesTags: (result, error, { assignmentId }) => [
        { type: "reports", id: `trust-score-assignment-${assignmentId}` },
      ],
    }),

    /**
     * Получить отчёт об окружении выполнения задания (assignment_environment)
     */
    getAssignmentEnvironmentReport: builder.query<AiReportData, { attemptId: number; forceRegenerate?: boolean }>({
      query: ({ attemptId, forceRegenerate = false }) => {
        const params = new URLSearchParams();
        params.append("force_regenerate", String(forceRegenerate));
        return {
          url: `/assignment_environment/${attemptId}?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response: AiReportData[] | AiReportData): AiReportData => {
        if (Array.isArray(response)) return response[0];
        return response;
      },
      providesTags: (result, error, { attemptId }) =>
        result
          ? [{ type: "reports", id: `assignment_environment-${attemptId}` }]
          : ["reports"],
    }),

    /**
     * AI отчёт о целостности по конкретному заданию
     */
    getAssignmentIntegrityReport: builder.query<
      AssignmentIntegrityReport,
      { assignmentId: number; forceRegenerate?: boolean }
    >({
      query: ({ assignmentId, forceRegenerate = false }) => ({
        url: `assignment/statistic/${assignmentId}`,
        method: "GET",
        params: { force_regenerate: forceRegenerate },
      }),
      providesTags: (result, error, { assignmentId }) =>
        result
          ? [{ type: "reports", id: `assignment-integrity-${assignmentId}` }]
          : ["reports"],
    }),

    /**
     * Агрегированный AI отчёт о целостности по всем заданиям школы
     */
    getSchoolAssignmentIntegrityReport: builder.query<
      AssignmentIntegrityReport,
      { schoolId: number; forceRegenerate?: boolean }
    >({
      query: ({ schoolId, forceRegenerate = false }) => ({
        url: `assignment/statistic/all`,
        method: "GET",
        params: { school_id: schoolId, force_regenerate: forceRegenerate },
      }),
      providesTags: (result, error, { schoolId }) =>
        result
          ? [{ type: "reports", id: `school-integrity-${schoolId}` }]
          : ["reports"],
    }),

    /**
     * Отправить feedback по evaluation отчёту
     */
    postAiReportFeedback: builder.mutation<
      PostAiReportFeedbackResponse,
      { attemptId: number; payload: PostAiReportFeedbackRequest }
    >({
      query: ({ attemptId, payload }) => ({
        url: `/evaluation/${attemptId}/feedback`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (result, error, { attemptId }) => [
        { type: "reports", id: `evaluation-${attemptId}` },
      ],
    }),
  }),
});

export const {
  useGetAiReportByIdQuery,
  useGetAiReportQuery,
  useGetAllAiReportsQuery,
  useGetTrustScoreByAttemptQuery,
  useGetTrustScoresByAssignmentQuery,
  useGetAssignmentEnvironmentReportQuery,
  useGetAssignmentIntegrityReportQuery,
  useGetSchoolAssignmentIntegrityReportQuery,
  usePostAiReportFeedbackMutation,
} = reportApi;

// Re-export types for convenience
export type { AiReportData, AiReportType, AllAiReports };
